/**
 * Shipments UI Types
 * Frontend mirror of backend types from @lognai/plugins/shipments
 */

import type { ListQueryParams } from '@lognai/frontend-core';
import type { CustomFieldValues } from '@lognai/plugin-sdk';

// ============================================================================
// Type Unions (String Literals)
// ============================================================================

export type ShipmentType = 'fcl' | 'lcl' | 'air' | 'courier' | 'road' | 'roro' | 'break_bulk';
export type ShipmentDirection = 'import' | 'export' | 'domestic';
export type ShipmentStatus = 
  | 'draft'
  | 'booked'
  | 'in_transit'
  | 'arrived'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'on_hold';

export type LegStatus = 'planned' | 'in_transit' | 'arrived' | 'completed' | 'cancelled';
export type TransportMode = 'sea' | 'air' | 'road' | 'rail' | 'barge';
export type ContainerSize = '20ft' | '40ft' | '40hc' | '45hc' | 'other';
export type ContainerType = 'dry' | 'reefer' | 'open_top' | 'flat_rack' | 'tank' | 'other';
export type ContainerStatus = 
  | 'empty'
  | 'loaded'
  | 'in_transit'
  | 'arrived'
  | 'delivered'
  | 'returned';

/** The document types the platform ships with. Mirrors the backend union. */
export type BuiltInShipmentDocumentType =
  | 'bill_of_lading'
  | 'house_bill_of_lading'
  | 'master_bill_of_lading'
  | 'sea_waybill'
  | 'airway_bill'
  | 'cmr_waybill'
  | 'commercial_invoice'
  | 'packing_list'
  | 'certificate_of_origin'
  | 'insurance_certificate'
  | 'customs_declaration'
  | 'dangerous_goods_declaration'
  | 'delivery_order'
  | 'shipping_instruction'
  /**
   * @deprecated Superseded by company-defined document types. No picker offers
   * it any more; the code and its label survive so documents stored under it
   * before the switch keep rendering.
   */
  | 'other';

/**
 * A document type code: one of the built-ins, or the `code` of a company-defined
 * type from the document type catalog.
 *
 * The open `string` arm lets every `documentType` / `requiredDocuments` /
 * `linkedDocumentTypes` field carry custom codes without changing its
 * declaration; the `& NonNullable<unknown>` intersection keeps the built-in
 * literals in autocomplete instead of collapsing the whole union to `string`.
 *
 * Never index `DOCUMENT_TYPE_LABELS` with one of these — use `labelOf()` from
 * `useDocumentTypeCatalog()`, which also resolves custom and deleted codes.
 */
export type ShipmentDocumentType = BuiltInShipmentDocumentType | (string & NonNullable<unknown>);

export type DocumentStatus = 
  | 'draft'
  | 'issued'
  | 'submitted'
  | 'approved'
  | 'expired'
  | 'cancelled';

export type AttachmentStatus = 'active' | 'archived' | 'superseded';

export type TrackingEventType =
  | 'booking_confirmed'
  | 'cargo_received'
  | 'loaded'
  | 'departed'
  | 'in_transit'
  | 'transshipment'
  | 'arrived'
  | 'unloaded'
  | 'customs_cleared'
  | 'out_for_pickup'
  | 'out_for_delivery'
  | 'delivered'
  | 'exception'
  | 'on_hold'
  | 'released'
  | 'other';

export type PartyRole =
  | 'shipper'
  | 'consignee'
  | 'carrier'
  | 'freight_forwarder'
  | 'customs_broker'
  | 'notify_party';

export type Priority = 'low' | 'normal' | 'high' | 'urgent';

/** An Incoterm from the company's Incoterms master list (master data), as Freight RFQ and TRFs take it. */
export type Incoterm =
  | 'EXW'
  | 'FCA'
  | 'CPT'
  | 'CIP'
  | 'DAP'
  | 'DPU'
  | 'DDP'
  | 'FAS'
  | 'FOB'
  | 'CFR'
  | 'CIF'
  | (string & {});

// ============================================================================
// Embedded Subdocument Interfaces
// ============================================================================

export interface ShipmentParty {
  role: PartyRole;
  companyId?: string;
  companyName: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
}

export interface HazardousDetails {
  unNumber?: string;
  class?: string;
  packingGroup?: string;
  properShippingName?: string;
  emergencyContact?: string;
}

export type WeightUnit = 'kg' | 'lb' | 'mt';
export type VolumeUnit = 'cbm' | 'cft';
export type TemperatureUnit = 'celsius' | 'fahrenheit';

export type DimensionUnit = 'cm' | 'in' | 'm';

/**
 * One kind of goods: N identical pieces with their own HS code, weight and
 * dimensions. Weight and dimensions are PER PIECE. Mirrors `CargoItem` in
 * plugin-shipments; `totalWeight` / `totalVolumeCbm` are server-derived.
 */
export interface CargoItem {
  id: string;
  description?: string;
  hsCode?: string;
  packageType?: string;
  packageCount: number;
  weightPerPiece?: number;
  weightUnit?: WeightUnit;
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: DimensionUnit;
  totalWeight?: number;
  totalVolumeCbm?: number;
  productId?: string;
  sku?: string;
  orderId?: string;
  orderNumber?: string;
  orderType?: 'purchase' | 'sales';
  orderLineItemId?: string;
  shipmentOrderLineId?: string;
}

export interface CargoDetails {
  description?: string;
  hsCode?: string;
  packageCount?: number;
  packageType?: string;
  grossWeight?: number;
  grossWeightUnit?: WeightUnit;
  volume?: number;
  volumeUnit?: VolumeUnit;
  chargeableWeight?: number;
  /** When present, the flat totals above are derived from these. */
  items?: CargoItem[];
}

// ============================================================================
// Entity Interfaces
// ============================================================================

/** Access a partner company holds on one shipment, granted on its Partners tab. */
export type ShipmentViewerAccessLevel = 'NONE' | 'READ_ONLY' | 'READ_WRITE' | 'FULL';

export interface Shipment {
  id: string;
  companyId: string;
  /** Access tags, `key:value` (e.g. `businessUnit:KIU CTS`). Absent means none. */
  scopeKeys?: string[];
  shipmentNumber: string;
  type: ShipmentType;
  direction: ShipmentDirection;
  status: ShipmentStatus;
  priority?: Priority;
  parties?: ShipmentParty[];
  originLocation?: string;
  originCountry?: string;
  destinationLocation?: string;
  destinationCountry?: string;
  bookingDate?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  deliveryDate?: string;
  /** POD discharge (journeyStatus 18). Starts the demurrage clock. */
  dischargeDate?: string;
  /** Gate-out for delivery (journeyStatus 19). Ends demurrage, starts detention. */
  gateOutDate?: string;
  /** Empty container returned (journeyStatus 21). Ends detention. */
  emptyReturnDate?: string;
  cargo?: CargoDetails;
  isHazardous: boolean;
  hazardousDetails?: HazardousDetails;
  incoterm?: Incoterm;
  currency?: string;
  freightCost?: number;
  insuranceCost?: number;
  totalCost?: number;
  chargesTotal?: number;
  orderIds?: string[];
  customerReference?: string;
  carrierReference?: string;
  /** Bill of Lading (ocean modes). Mirrored to the legacy `carrierReference`. */
  blNumber?: string;
  /** Air Waybill (air mode). Mirrored to the legacy `carrierReference`. */
  awbNumber?: string;
  /** Carrier SCAC — links an ocean shipment to the carrier master (uppercased). */
  carrierScac?: string;
  /**
   * Carrier master id. The mode-agnostic link, and the only safe one for air:
   * IATA designators are recycled between airlines.
   */
  carrierId?: string;
  /**
   * Carrier display name, resolved server-side from the carrier master on read.
   * Never sent back on write — it is derived, not stored.
   */
  carrierName?: string;
  /** Current tracked ETA, attached to list rows by the API. `estimatedArrival` is the frozen first promise. */
  etaLive?: string;
  /** Container numbers, attached to list rows by the API (not on the detail record). */
  containerNumbers?: string[];
  /** The company's own custom field values, keyed by field key. Absent for partners below full access. */
  customFields?: CustomFieldValues;
  bookingReference?: string;
  legCount: number;
  containerCount: number;
  documentCount: number;
  trackingEventCount: number;
  /**
   * Journey phase (0–21) derived from tracking milestones — the most-advanced
   * ACTual milestone reached. Advance-only. Drives the radar lane.
   */
  trackingPhase?: number;
  /** When the milestones currently on this shipment were created. */
  milestonesInitializedAt?: string;
  /**
   * The milestone template the current milestone set came from; `null` means
   * the built-in default set was used because nothing matched at the time.
   */
  milestoneSourceConfigId?: string | null;
  /** That template's name as it stood when the milestones were created. */
  milestoneSourceConfigName?: string | null;
  /**
   * The source the owner was offered and declined to switch to — a config id,
   * or `MILESTONE_SOURCE_DEFAULT` for the built-in default set.
   */
  milestoneDeclinedSourceId?: string | null;
  /**
   * The access this company was granted on the shipment via its Partners tab.
   * Server-derived per request; absent when the shipment is our own. `FULL` is
   * what lets a partner manage the shipment's milestones, not just record
   * progress on them.
   */
  viewerAccessLevel?: ShipmentViewerAccessLevel;
  /**
   * Provenance the server already stores and no screen read.
   *
   * A shipment created from a freight-RFQ award carries `freightRfqRfqId`,
   * `freightRfqLaneId`, `freightRfqQuoteOptionId` and `awardedCarrierName`
   * here, and the RFQ lane stores `linkedShipmentId` back — a complete
   * bidirectional link, surfaced nowhere (QA ledger SHIP-001). Typed loosely
   * because the map is open: only the keys a screen actually uses are named.
   */
  metadata?: Record<string, unknown> & {
    freightRfqRfqId?: string;
    freightRfqLaneId?: string;
    freightRfqQuoteOptionId?: string;
    freightRfqCarrierCompanyId?: string;
    awardedCarrierName?: string;
  };
  notes?: string;
  internalNotes?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/** Carrier support flags (which reference numbers the carrier honors). */
export interface CarrierSupports {
  billOfLading: boolean;
  bookingNumber: boolean;
  containerNumber: boolean;
}

/** Transport mode a carrier operates in. */
export type CarrierType = 'ocean' | 'air';

/**
 * Global carrier reference — shipping lines and airlines. Mirrors the shipments
 * backend `Carrier`.
 *
 * Mode-specific fields are optional and `type` discriminates: ocean carriers
 * carry a SCAC, air carriers an IATA code and AWB prefix. Neither code is
 * unique for air, so `id` is the reference stored on shipments and legs.
 */
export interface Carrier {
  id: string;
  name: string;
  type: CarrierType;
  aliases: string[];
  active: boolean;
  region?: string;
  notes?: string;

  // Ocean only
  scac?: string; // primary SCAC, uppercased
  supports?: CarrierSupports;

  // Air only
  iata?: string; // IATA airline designator, 2 chars uppercased. Not unique.
  awbPrefix?: string; // air waybill numeric prefix, zero-padded to 3 digits
  country?: string;
  sourceId?: string;
}

export interface ShipmentLeg {
  id: string;
  companyId: string;
  shipmentId: string;
  legNumber: number;
  status: LegStatus;
  transportMode: TransportMode;
  carrierId?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  flightNumber?: string;
  vehiclePlate?: string;
  originLocation: string;
  originCountry: string;
  destinationLocation: string;
  destinationCountry: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentContainer {
  id: string;
  companyId: string;
  shipmentId: string;
  containerNumber: string;
  sealNumber?: string;
  size: ContainerSize;
  containerType: ContainerType;
  /**
   * ISO equipment code — `40HC`, `20GP`, `40RF`.
   *
   * Optional and additive. Equipment is what selects a D&D contract term, and
   * `size` + `containerType` is a coarser vocabulary than a contract is scoped
   * by. The engine derives one when this is absent and blocks the container
   * when neither works.
   */
  isoEquipmentCode?: string;
  status: ContainerStatus;
  grossWeight?: number;
  grossWeightUnit?: WeightUnit;
  tareWeight?: number;
  maxPayload?: number;
  isTemperatureControlled: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  temperatureUnit?: TemperatureUnit;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShipmentDocumentAttachment {
  id: string;
  s3Key: string;
  s3Bucket: string;
  fileName: string;
  mimeType: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;

  // Version control & status fields
  version?: number;
  isLatest?: boolean;
  status?: AttachmentStatus;
  previousVersionId?: string;
  rootAttachmentId?: string;
  changeReason?: string;
  archivedAt?: string;
  archivedBy?: string;
}

/** Every action that can appear on a document's unified timeline. */
export type DocumentTimelineAction =
  | 'document_created'
  | 'details_updated'
  | 'details_reverted'
  | 'document_deleted'
  | 'document_restored'
  | 'file_added'
  | 'file_version_added'
  | 'file_removed'
  | 'file_restored';

export interface DocumentTimelineChange {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
  label?: string;
}

export interface DocumentDetailsSnapshot {
  documentType: ShipmentDocumentType;
  referenceNumber: string;
  title: string;
  status: DocumentStatus;
  issuedDate?: string | Date;
  expiryDate?: string | Date;
  issuedBy?: string;
  notes?: string;
}

export interface DocumentFileSnapshot {
  attachmentId: string;
  fileName: string;
  mimeType: string;
  size: number;
  version: number;
  rootAttachmentId: string;
}

/**
 * One entry on a document's single history: detail edits and file changes share
 * this shape so the UI renders them in one list.
 */
export interface DocumentTimelineEntry {
  id: string;
  companyId: string;
  shipmentId: string;
  documentId: string;
  documentType: ShipmentDocumentType;
  sequence: number;
  action: DocumentTimelineAction;
  actor: string;
  actorType: 'user' | 'system' | 'partner';
  timestamp: string;
  summary?: string;
  changes?: DocumentTimelineChange[];
  detailsSnapshot?: DocumentDetailsSnapshot;
  fileSnapshot?: DocumentFileSnapshot;
  archivedAttachmentIds?: string[];
  metadata?: Record<string, unknown>;
  /** This entry already describes the document's current state */
  isCurrent: boolean;
  /** The document can be reverted to this entry */
  isRevertible: boolean;
}

export interface ShipmentDocument {
  id: string;
  companyId: string;
  shipmentId: string;
  documentType: ShipmentDocumentType;
  referenceNumber: string;
  title: string;
  status: DocumentStatus;
  issuedDate?: string | Date;
  expiryDate?: string | Date;
  issuedBy?: string;
  attachments: ShipmentDocumentAttachment[];
  notes?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  /**
   * Set on soft-deleted documents. They are excluded from the normal list; the
   * "Deleted" view fetches them so their history can be read and the deletion
   * undone.
   */
  deletedAt?: string | Date;
  deletedBy?: string;
  /**
   * Set by the API only when the caller is a partner on this shipment: what the
   * shipment owner's document configuration lets them do with this document type.
   * 'none' still means the document (and whether a file exists) is visible —
   * 'read' allows opening/downloading it, 'write' also allows changing it.
   * Absent for the shipment owner, who is unrestricted.
   */
  partnerAccessLevel?: PartnerDocumentAccessLevel;
}

export interface ShipmentTrackingEvent {
  id: string;
  companyId: string;
  shipmentId: string;
  legId?: string;
  eventType: TrackingEventType;
  description: string;
  location?: string;
  country?: string;
  eventDate: string;
  source?: string;
  sourceReference?: string;
  notes?: string;
  /**
   * Provider-supplied extras. Ocean-tracking events carry
   * `latitude`/`longitude`/`vesselName` here; manual events have none.
   */
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// ============================================================================
// DTO Interfaces
// ============================================================================

export interface CreateShipmentDto {
  /** Access tags to set, `{ [key]: values }`. Omitted on update = unchanged; empty clears. */
  scope?: Record<string, string[]>;
  /** Custom field values to set; `null` or `''` clears a field. Checked server-side against the company's fields. */
  customFields?: Record<string, unknown>;
  type: ShipmentType;
  direction: ShipmentDirection;
  priority?: Priority;
  parties?: ShipmentParty[];
  originLocation?: string;
  originCountry?: string;
  destinationLocation?: string;
  destinationCountry?: string;
  bookingDate?: string;
  estimatedDeparture?: string;
  estimatedArrival?: string;
  cargo?: CargoDetails;
  isHazardous?: boolean;
  hazardousDetails?: HazardousDetails;
  incoterm?: Incoterm;
  currency?: string;
  freightCost?: number;
  insuranceCost?: number;
  orderIds?: string[];
  customerReference?: string;
  carrierReference?: string;
  /** Bill of Lading (ocean modes). Mirrored to the legacy `carrierReference`. */
  blNumber?: string;
  /** Air Waybill (air mode). Mirrored to the legacy `carrierReference`. */
  awbNumber?: string;
  carrierScac?: string;
  carrierId?: string;
  bookingReference?: string;
  /**
   * Containers to put on the shipment at create (FCL/LCL only, ISO 6346).
   * Created with size/type `other`; tracking fills the rest.
   */
  containerNumbers?: string[];
  notes?: string;
  internalNotes?: string;
}

export interface UpdateShipmentDto {
  /** Access tags to set, `{ [key]: values }`. Omitted on update = unchanged; empty clears. */
  scope?: Record<string, string[]>;
  /** Custom field values to set; `null` or `''` clears a field. Checked server-side against the company's fields. */
  customFields?: Record<string, unknown>;
  direction?: ShipmentDirection;
  priority?: Priority;
  parties?: ShipmentParty[];
  originLocation?: string;
  originCountry?: string;
  destinationLocation?: string;
  destinationCountry?: string;
  bookingDate?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  deliveryDate?: string;
  cargo?: CargoDetails;
  isHazardous?: boolean;
  hazardousDetails?: HazardousDetails;
  incoterm?: Incoterm;
  currency?: string;
  freightCost?: number;
  insuranceCost?: number;
  orderIds?: string[];
  customerReference?: string;
  carrierReference?: string;
  /** Bill of Lading (ocean modes). Mirrored to the legacy `carrierReference`. */
  blNumber?: string;
  /** Air Waybill (air mode). Mirrored to the legacy `carrierReference`. */
  awbNumber?: string;
  carrierScac?: string;
  carrierId?: string;
  bookingReference?: string;
  notes?: string;
  internalNotes?: string;
}

export interface UpdateShipmentStatusDto {
  status: ShipmentStatus;
  reason?: string;
}

export interface CreateShipmentLegDto {
  transportMode: TransportMode;
  carrierId?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  flightNumber?: string;
  vehiclePlate?: string;
  originLocation: string;
  originCountry: string;
  destinationLocation: string;
  destinationCountry: string;
  estimatedDeparture?: string;
  estimatedArrival?: string;
  notes?: string;
}

export interface UpdateShipmentLegDto {
  status?: LegStatus;
  carrierId?: string;
  carrierName?: string;
  vesselName?: string;
  voyageNumber?: string;
  flightNumber?: string;
  vehiclePlate?: string;
  originLocation?: string;
  originCountry?: string;
  destinationLocation?: string;
  destinationCountry?: string;
  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  notes?: string;
}

export interface CreateShipmentContainerDto {
  containerNumber: string;
  sealNumber?: string;
  size: ContainerSize;
  containerType: ContainerType;
  /** ISO equipment code (`40HC`). Drives D&D contract-term selection. */
  isoEquipmentCode?: string;
  grossWeight?: number;
  grossWeightUnit?: WeightUnit;
  tareWeight?: number;
  maxPayload?: number;
  isTemperatureControlled?: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  temperatureUnit?: TemperatureUnit;
  notes?: string;
}

export interface UpdateShipmentContainerDto {
  sealNumber?: string;
  size?: ContainerSize;
  containerType?: ContainerType;
  /** ISO equipment code (`40HC`). Drives D&D contract-term selection. */
  isoEquipmentCode?: string;
  status?: ContainerStatus;
  grossWeight?: number;
  grossWeightUnit?: WeightUnit;
  tareWeight?: number;
  maxPayload?: number;
  isTemperatureControlled?: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  temperatureUnit?: TemperatureUnit;
  notes?: string;
}

export interface CreateShipmentDocumentDto {
  documentType: ShipmentDocumentType;
  referenceNumber?: string;
  title?: string;
  status?: DocumentStatus;
  issuedDate?: string;
  expiryDate?: string;
  issuedBy?: string;
  notes?: string;
}

export interface UpdateShipmentDocumentDto {
  documentType?: ShipmentDocumentType;
  referenceNumber?: string;
  title?: string;
  status?: DocumentStatus;
  issuedDate?: string | Date;
  expiryDate?: string | Date;
  issuedBy?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  changeSummary?: string;
}

export interface CreateShipmentTrackingEventDto {
  eventType: TrackingEventType;
  description: string;
  location?: string;
  country?: string;
  eventDate: string;
  legId?: string;
  source?: string;
  sourceReference?: string;
  notes?: string;
}

// ============================================================================
// Filter Interfaces
// ============================================================================

export interface ShipmentFilter {
  /** Planned date ranges as ISO timestamps, inclusive of both boundaries. */
  estimatedArrival?: { from?: string; to?: string };
  estimatedDeparture?: { from?: string; to?: string };
  status?: ShipmentStatus;
  type?: ShipmentType;
  direction?: ShipmentDirection;
  priority?: Priority;
  search?: string;
  /** Only the caller's watched shipments (server-side, `filter[watchlist]=true`). */
  watchlist?: boolean;
  /**
   * Filter by the company's custom fields: `{ [key]: value | value[] }`. Several
   * values mean "any of"; `CUSTOM_FIELD_NO_VALUE` means "not set". Serialised as
   * repeated `filter[cf][key]=…` params by the API client, not by the shared query
   * builder, which would comma-join an array into one value.
   */
  cf?: Record<string, string | string[]>;

  // ── "More filters" drawer (server: shipments `operations/shipments/list-filters.ts`) ──
  /** Partial origin / destination text (port, city, address). */
  origin?: string;
  destination?: string;
  originCountry?: string;
  destinationCountry?: string;
  /** Carrier master id and/or SCAC of the carrier picked in the drawer. */
  carrierId?: string;
  carrierScac?: string;
  /** Partial party name. */
  shipper?: string;
  consignee?: string;
  blNumber?: string;
  awbNumber?: string;
  bookingReference?: string;
  customerReference?: string;
  incoterm?: string;
  hazardous?: 'true' | 'false';
  hasTracking?: 'true' | 'false';
  /** Date ranges as `yyyy-mm-dd` (local); widened to start/end of day when sent. */
  bookedFrom?: string;
  bookedTo?: string;
  etdFrom?: string;
  etdTo?: string;
  atdFrom?: string;
  atdTo?: string;
  etaFrom?: string;
  etaTo?: string;
  ataFrom?: string;
  ataTo?: string;
  deliveredFrom?: string;
  deliveredTo?: string;
  createdFrom?: string;
  createdTo?: string;
  /**
   * Access tags, `key:value`: any value within a tag, every tag across. Sent as
   * repeated top-level `tag=` params by the API client.
   */
  tag?: string[];
}

/** The filter keys the "More filters" drawer owns (the bar owns the rest). */
export const SHIPMENT_DRAWER_FILTER_KEYS = [
  'origin', 'destination', 'originCountry', 'destinationCountry', 'carrierId', 'carrierScac',
  'shipper', 'consignee', 'blNumber', 'awbNumber', 'bookingReference', 'customerReference',
  'incoterm', 'hazardous', 'hasTracking',
  'bookedFrom', 'bookedTo', 'etdFrom', 'etdTo', 'atdFrom', 'atdTo', 'etaFrom', 'etaTo',
  'ataFrom', 'ataTo', 'deliveredFrom', 'deliveredTo', 'createdFrom', 'createdTo',
] as const satisfies readonly (keyof ShipmentFilter)[];

export type ShipmentDrawerFilterKey = (typeof SHIPMENT_DRAWER_FILTER_KEYS)[number];

export type ShipmentFilters = ListQueryParams<ShipmentFilter>;

export interface ShipmentKpis {
  totalShipments: number;
  draftCount: number;
  bookedCount: number;
  inTransitCount: number;
  arrivedCount: number;
  deliveredCount: number;
  completedCount: number;
  cancelledCount: number;
  onHoldCount: number;
}

// ============================================================================
// Label Maps (for UI Display)
// ============================================================================

export const SHIPMENT_TYPE_LABELS: Record<ShipmentType, string> = {
  fcl: 'FCL',
  lcl: 'LCL',
  air: 'Air',
  courier: 'Courier',
  road: 'Road',
  roro: 'RORO',
  break_bulk: 'Break Bulk',
};

export const SHIPMENT_DIRECTION_LABELS: Record<ShipmentDirection, string> = {
  import: 'Import',
  export: 'Export',
  domestic: 'Domestic',
};

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  draft: 'Draft',
  booked: 'Booked',
  in_transit: 'In Transit',
  arrived: 'Arrived',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  sea: 'Sea',
  air: 'Air',
  road: 'Road',
  rail: 'Rail',
  barge: 'Barge',
};

export const LEG_STATUS_LABELS: Record<LegStatus, string> = {
  planned: 'Planned',
  in_transit: 'In Transit',
  arrived: 'Arrived',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const CONTAINER_SIZE_LABELS: Record<ContainerSize, string> = {
  '20ft': '20ft',
  '40ft': '40ft',
  '40hc': '40ft HC',
  '45hc': '45ft HC',
  other: 'Other',
};

export const CONTAINER_TYPE_LABELS: Record<ContainerType, string> = {
  dry: 'Dry',
  reefer: 'Reefer',
  open_top: 'Open Top',
  flat_rack: 'Flat Rack',
  tank: 'Tank',
  other: 'Other',
};

export const CONTAINER_STATUS_LABELS: Record<ContainerStatus, string> = {
  empty: 'Empty',
  loaded: 'Loaded',
  in_transit: 'In Transit',
  arrived: 'Arrived',
  delivered: 'Delivered',
  returned: 'Returned',
};

/**
 * Labels for the built-in types only. Custom types carry their own `name`, so
 * reach for `labelOf()` from `useDocumentTypeCatalog()` rather than this map
 * whenever the code could have come from a document, config or milestone.
 */
export const DOCUMENT_TYPE_LABELS: Record<BuiltInShipmentDocumentType, string> = {
  bill_of_lading: 'Bill of Lading',
  house_bill_of_lading: 'House Bill of Lading',
  master_bill_of_lading: 'Master Bill of Lading',
  sea_waybill: 'Sea Waybill',
  airway_bill: 'Airway Bill',
  cmr_waybill: 'CMR Waybill',
  commercial_invoice: 'Commercial Invoice',
  packing_list: 'Packing List',
  certificate_of_origin: 'Certificate of Origin',
  insurance_certificate: 'Insurance Certificate',
  customs_declaration: 'Customs Declaration',
  dangerous_goods_declaration: 'Dangerous Goods Declaration',
  delivery_order: 'Delivery Order',
  shipping_instruction: 'Shipping Instruction',
  other: 'Other',
};

// Common document types available for all shipment modes.
// `other` is deliberately absent — a company defines a real type for whatever it
// used to cover (see DocumentTypeDialog), and legacy `other` documents keep
// rendering via DOCUMENT_TYPE_LABELS without being offered again.
const COMMON_DOCUMENT_TYPES: BuiltInShipmentDocumentType[] = [
  'commercial_invoice', 'packing_list', 'certificate_of_origin',
  'insurance_certificate', 'customs_declaration', 'dangerous_goods_declaration',
  'delivery_order', 'shipping_instruction',
];

// Built-in document types relevant to each shipment type. Company-defined types
// carry their own `applicableShipmentTypes`; the catalog merges the two.
export const DOCUMENT_TYPES_BY_SHIPMENT_TYPE: Record<string, BuiltInShipmentDocumentType[]> = {
  fcl: ['bill_of_lading', 'house_bill_of_lading', 'master_bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
  lcl: ['bill_of_lading', 'house_bill_of_lading', 'master_bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
  air: ['airway_bill', ...COMMON_DOCUMENT_TYPES],
  courier: ['airway_bill', ...COMMON_DOCUMENT_TYPES],
  road: ['cmr_waybill', ...COMMON_DOCUMENT_TYPES],
  roro: ['bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
  break_bulk: ['bill_of_lading', 'sea_waybill', ...COMMON_DOCUMENT_TYPES],
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  submitted: 'Submitted',
  approved: 'Approved',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export const TRACKING_EVENT_TYPE_LABELS: Record<TrackingEventType, string> = {
  booking_confirmed: 'Booking Confirmed',
  cargo_received: 'Cargo Received',
  loaded: 'Loaded',
  departed: 'Departed',
  in_transit: 'In Transit',
  transshipment: 'Transshipment',
  arrived: 'Arrived',
  unloaded: 'Unloaded',
  customs_cleared: 'Customs Cleared',
  out_for_pickup: 'Out for Pickup',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  exception: 'Exception',
  on_hold: 'On Hold',
  released: 'Released',
  other: 'Other',
};

export const PARTY_ROLE_LABELS: Record<PartyRole, string> = {
  shipper: 'Shipper',
  consignee: 'Consignee',
  carrier: 'Carrier',
  freight_forwarder: 'Freight Forwarder',
  customs_broker: 'Customs Broker',
  notify_party: 'Notify Party',
};

export const INCOTERM_LABELS: Record<Incoterm, string> = {
  EXW: 'EXW - Ex Works',
  FCA: 'FCA - Free Carrier',
  CPT: 'CPT - Carriage Paid To',
  CIP: 'CIP - Carriage and Insurance Paid To',
  DAP: 'DAP - Delivered at Place',
  DPU: 'DPU - Delivered at Place Unloaded',
  DDP: 'DDP - Delivered Duty Paid',
  FAS: 'FAS - Free Alongside Ship',
  FOB: 'FOB - Free on Board',
  CFR: 'CFR - Cost and Freight',
  CIF: 'CIF - Cost, Insurance and Freight',
};

// Any-mode incoterms (ICC Incoterms 2020)
const ANY_MODE_INCOTERMS: Incoterm[] = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];
// Maritime-only incoterms
const MARITIME_INCOTERMS: Incoterm[] = ['FAS', 'FOB', 'CFR', 'CIF'];

/**
 * Every Incoterm a shipment can carry. Config editors and the rule simulator
 * all filter on this list — a term missing here is a term no milestone or
 * document rule can ever be targeted at.
 */
export const ALL_INCOTERMS: Incoterm[] = [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS];

// Incoterms available per shipment type
export const INCOTERMS_BY_SHIPMENT_TYPE: Record<string, Incoterm[]> = {
  fcl: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
  lcl: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
  air: ANY_MODE_INCOTERMS,
  courier: ANY_MODE_INCOTERMS,
  road: ANY_MODE_INCOTERMS,
  roro: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
  break_bulk: [...ANY_MODE_INCOTERMS, ...MARITIME_INCOTERMS],
};

// ============================================================================
// Milestone Types
// ============================================================================

export type MilestoneType = 'documentation' | 'physical' | 'financial' | 'regulatory';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';
export type DocumentSatisfactionStatus = 'none_required' | 'pending_upload' | 'pending_approval' | 'satisfied';

export type TrackingSatisfactionStatus = 'none_required' | 'pending_event' | 'satisfied';

/**
 * How a milestone's completion landed relative to its ETA. Derived server-side
 * from the completion time and read back off the audit log — mirrors
 * `EtaFulfillmentStatus` in shipments/src/types/milestones.ts.
 */
export type EtaFulfillmentStatus = 'before_eta' | 'at_eta' | 'after_eta';

export const ETA_FULFILLMENT_STATUS_LABELS: Record<EtaFulfillmentStatus, string> = {
  before_eta: 'Completed Before ETA',
  at_eta: 'Completed At ETA',
  after_eta: 'Completed After ETA (Overdue)',
};

export interface MilestonePrerequisite {
  dependsOnMilestoneId: string;
  requiredStatus?: 'completed' | 'satisfied';
}

export interface MilestoneDefinition {
  id: string;
  name: string;
  description?: string | null;
  owner?: string | null;
  updater?: string | null;
  type: MilestoneType;
  order: number;
  linkedDocumentTypes?: ShipmentDocumentType[];
  autoCompleteOnDocumentUpload?: boolean;
  requireDocumentApproval?: boolean;
  onDocumentRemovalBehavior?: 'revert_to_pending' | 'revert_to_in_progress' | 'flag_warning' | 'none';
  linkedTrackingEventTypes?: TrackingEventType[];
  trackingEventLocation?: string | null;
  trackingEventCountry?: string | null;
  trackingEventSource?: string | null;
  trackingEventMatchKeyword?: string | null;
  autoCompleteOnTrackingEvent?: boolean;
  prerequisites?: MilestonePrerequisite[];
}

export interface MilestonePhase {
  id: string;
  name: string;
  order: number;
  milestones: MilestoneDefinition[];
}

export interface MilestoneConfig {
  id: string;
  companyId: string;
  name?: string;
  description?: string | null;
  shipmentModes?: ShipmentType[];
  shipmentKinds?: ShipmentDirection[];
  incoterms?: string[];
  isHazardous?: boolean | null;
  customCriteria?: Record<string, any>;
  originCountries?: string[];
  destinationCountries?: string[];
  priorityWeight?: number;
  shipmentType?: ShipmentType;
  direction?: ShipmentDirection;
  phases: MilestonePhase[];
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/** A milestone config template owned by a partner company, shared read-only. */
export interface SharedMilestoneConfigEntry {
  config: MilestoneConfig;
  ownerCompanyId: string;
  ownerCompanyName: string;
  readOnly: true;
}

export interface MilestoneLastStatusChange {
  from: MilestoneStatus;
  to: MilestoneStatus;
  at: string;
  by: string;
}

export interface ShipmentMilestone {
  id: string;
  companyId: string;
  shipmentId: string;
  phaseId: string;
  phaseName: string;
  milestoneDefinitionId: string;
  milestoneName: string;
  milestoneType: MilestoneType;
  owner?: string;
  updater?: string;
  status: MilestoneStatus;
  completedAt?: string;
  completedBy?: string;
  createdBy?: string;
  notes?: string;
  order: number;
  phaseOrder: number;
  createdAt: string;
  updatedAt: string;
  // ETA tracking
  eta?: string;
  etaSetBy?: string;
  etaSetAt?: string;
  // Latest status change snapshot
  lastStatusChange?: MilestoneLastStatusChange;
  linkedDocumentTypes?: ShipmentDocumentType[];
  linkedDocumentIds?: string[];
  documentSatisfactionStatus?: DocumentSatisfactionStatus;
  isAutoCompleted?: boolean;
  autoCompleteOnDocumentUpload?: boolean;
  requireDocumentApproval?: boolean;
  onDocumentRemovalBehavior?: 'revert_to_pending' | 'revert_to_in_progress' | 'flag_warning' | 'none';
  linkedTrackingEventTypes?: TrackingEventType[];
  trackingEventLocation?: string;
  trackingEventCountry?: string;
  trackingEventSource?: string;
  trackingEventMatchKeyword?: string;
  autoCompleteOnTrackingEvent?: boolean;
  trackingSatisfactionStatus?: TrackingSatisfactionStatus;
  prerequisites?: MilestonePrerequisite[];
  version?: number;
  isManuallyOverridden?: boolean;
  /** Template this row came from; `null` for the built-in defaults or a hand-added milestone. */
  sourceConfigId?: string | null;
}

export interface SaveMilestoneConfigDto {
  id?: string;
  name?: string;
  description?: string | null;
  shipmentModes?: ShipmentType[];
  shipmentKinds?: ShipmentDirection[];
  incoterms?: string[];
  isHazardous?: boolean | null;
  customCriteria?: Record<string, any>;
  originCountries?: string[];
  destinationCountries?: string[];
  priorityWeight?: number;
  shipmentType?: ShipmentType;
  direction?: ShipmentDirection;
  phases: {
    id?: string;
    name: string;
    order: number;
    milestones: {
      id?: string;
      name: string;
      description?: string | null;
      owner?: string | null;
      updater?: string | null;
      type: MilestoneType;
      order: number;
      linkedDocumentTypes?: ShipmentDocumentType[];
      autoCompleteOnDocumentUpload?: boolean;
      requireDocumentApproval?: boolean;
      onDocumentRemovalBehavior?: 'revert_to_pending' | 'revert_to_in_progress' | 'flag_warning' | 'none';
      linkedTrackingEventTypes?: TrackingEventType[];
      trackingEventLocation?: string | null;
      trackingEventCountry?: string | null;
      trackingEventSource?: string | null;
      trackingEventMatchKeyword?: string | null;
      autoCompleteOnTrackingEvent?: boolean;
      prerequisites?: MilestonePrerequisite[];
    }[];
  }[];
}

export interface CreateShipmentMilestoneDto {
  phaseId: string;
  phaseName: string;
  milestoneName: string;
  milestoneType: MilestoneType;
  owner?: string;
  order: number;
  phaseOrder?: number;
  linkedDocumentTypes?: ShipmentDocumentType[];
  linkedDocumentIds?: string[];
  autoCompleteOnDocumentUpload?: boolean;
  requireDocumentApproval?: boolean;
  onDocumentRemovalBehavior?: 'revert_to_pending' | 'revert_to_in_progress' | 'flag_warning' | 'none';
  linkedTrackingEventTypes?: TrackingEventType[];
  trackingEventLocation?: string;
  trackingEventCountry?: string;
  trackingEventSource?: string;
  trackingEventMatchKeyword?: string;
  autoCompleteOnTrackingEvent?: boolean;
  prerequisites?: MilestonePrerequisite[];
}

export interface UpdateShipmentMilestoneDto {
  milestoneName?: string;
  milestoneType?: MilestoneType;
  owner?: string;
  status?: MilestoneStatus;
  order?: number | null;
  notes?: string;
  eta?: string;
  linkedDocumentTypes?: ShipmentDocumentType[];
  linkedDocumentIds?: string[];
  autoCompleteOnDocumentUpload?: boolean;
  requireDocumentApproval?: boolean;
  onDocumentRemovalBehavior?: 'revert_to_pending' | 'revert_to_in_progress' | 'flag_warning' | 'none';
  linkedTrackingEventTypes?: TrackingEventType[];
  trackingEventLocation?: string;
  trackingEventCountry?: string;
  trackingEventSource?: string;
  trackingEventMatchKeyword?: string;
  autoCompleteOnTrackingEvent?: boolean;
  prerequisites?: MilestonePrerequisite[];
  version?: number;
  isManuallyOverridden?: boolean;
}

export const MILESTONE_TYPE_LABELS: Record<MilestoneType, string> = {
  documentation: 'Documentation',
  physical: 'Physical',
  financial: 'Financial',
  regulatory: 'Regulatory',
};

export const MILESTONE_STATUS_LABELS: Record<MilestoneStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  skipped: 'Skipped',
};

export const DEFAULT_MILESTONE_PHASES: { name: string; order: number }[] = [
  { name: 'Pre-Shipment & Booking', order: 0 },
  { name: 'Export Origin Handling', order: 1 },
  { name: 'Main Transit', order: 2 },
  { name: 'Import Destination Operations', order: 3 },
  { name: 'Post-Delivery & Financials', order: 4 },
];

// ============================================================================
// Document Configuration Types
// ============================================================================

/** Per-document access level granted to a class of partners. */
export type PartnerDocumentAccessLevel = 'none' | 'read' | 'write';

/**
 * The caller's resolved access levels for one shipment's document types.
 *
 * Documents that exist carry their own `partnerAccessLevel`; this map is what
 * lets the required-but-not-yet-uploaded rows — which have no document behind
 * them — be resolved to a level too.
 */
export interface ShipmentDocumentAccess {
  /** false = owner (or any caller with no partner restrictions at all). */
  isPartner: boolean;
  partnerCompanyId?: string;
  /** Keyed by document type; anything absent means 'none'. */
  levels: Record<string, PartnerDocumentAccessLevel>;
}

/**
 * A grant on one document type, keyed by partner **type** (their functional
 * role): every partner of that type on a matching shipment inherits the level.
 */
export interface PartnerTypeDocumentPermission {
  /** Partner type code, upper-cased (e.g. 'FORWARDER'). */
  partnerType: string;
  accessLevel: PartnerDocumentAccessLevel;
}

/**
 * A grant aimed at one named partner company.
 *
 * @deprecated Superseded by {@link PartnerTypeDocumentPermission}; existing rows
 * are still honoured server-side but the editor no longer writes this shape.
 */
export interface PartnerCompanyDocumentPermission {
  partnerCompanyId: string;
  accessLevel: PartnerDocumentAccessLevel;
}

/** A single partner permission entry for a specific document type. */
export type PartnerDocumentPermission =
  | PartnerTypeDocumentPermission
  | PartnerCompanyDocumentPermission;

/** Narrowing helper — distinguishes type grants from legacy company grants. */
export const isPartnerTypePermission = (
  permission: PartnerDocumentPermission
): permission is PartnerTypeDocumentPermission =>
  typeof (permission as PartnerTypeDocumentPermission).partnerType === 'string' &&
  (permission as PartnerTypeDocumentPermission).partnerType.length > 0;

/**
 * Partner types available out of the box, mirroring the `PartnershipType` codes
 * the admin plugin assigns to a partnership. Custom codes in use across the
 * company's partner network are merged in at render time.
 */
export const PARTNER_TYPE_LABELS: Record<string, string> = {
  VENDOR: 'Vendor / Supplier',
  CUSTOMER: 'Customer',
  FORWARDER: 'Freight Forwarder',
  CARRIER: 'Carrier',
  '3PL': '3PL',
  CHA: 'Customs House Agent',
  WAREHOUSE_OPERATOR: 'Warehouse Operator',
  DELIVERY_AGENT: 'Delivery Agent',
  BANK: 'Bank',
};

export interface DocumentConfig {
  id: string;
  companyId: string;
  name?: string;
  description?: string;
  shipmentModes?: ShipmentType[];
  shipmentKinds?: ShipmentDirection[];
  incoterms?: string[];
  isHazardous?: boolean | null;
  customCriteria?: Record<string, any>;
  originCountries?: string[];
  destinationCountries?: string[];
  priorityWeight?: number;
  shipmentType?: ShipmentType;
  direction?: ShipmentDirection;
  requiredDocuments: ShipmentDocumentType[];
  /** Per-document-type partner access controls. */
  partnerDocumentAccess?: Record<string, PartnerDocumentPermission[]>;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SaveDocumentConfigDto {
  id?: string;
  name?: string;
  description?: string;
  shipmentModes?: ShipmentType[];
  shipmentKinds?: ShipmentDirection[];
  incoterms?: string[];
  isHazardous?: boolean | null;
  customCriteria?: Record<string, any>;
  originCountries?: string[];
  destinationCountries?: string[];
  priorityWeight?: number;
  shipmentType?: ShipmentType;
  direction?: ShipmentDirection;
  requiredDocuments: ShipmentDocumentType[];
  /** Per-document-type partner access controls. */
  partnerDocumentAccess?: Record<string, PartnerDocumentPermission[]>;
}

/** A document config template owned by a partner company, shared read-only. */
export interface SharedDocumentConfigEntry {
  config: DocumentConfig;
  ownerCompanyId: string;
  ownerCompanyName: string;
  readOnly: true;
}

// ============================================================================
// Company-Defined Document Types
// ============================================================================

/**
 * One entry in a company's document type catalog, usable everywhere a built-in
 * type is: as a document's type, in a config's required list and partner
 * grants, and as a milestone's linked-document condition.
 */
export interface ShipmentDocumentTypeDefinition {
  id: string;
  companyId: string;
  /** Immutable once created — everything references the type by this. */
  code: string;
  name: string;
  description?: string;
  /** Empty or absent means every shipment mode. */
  applicableShipmentTypes?: ShipmentType[];
  isActive: boolean;
  order?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDocumentTypeDto {
  name: string;
  /** Derived from `name` server-side when omitted. */
  code?: string;
  description?: string;
  applicableShipmentTypes?: ShipmentType[];
  isActive?: boolean;
  order?: number;
}

/** No `code` — see {@link ShipmentDocumentTypeDefinition.code}. */
export interface UpdateDocumentTypeDto {
  name?: string;
  description?: string;
  applicableShipmentTypes?: ShipmentType[];
  isActive?: boolean;
  order?: number | null;
}

/** A document type owned by a partner company, shared read-only. */
export interface SharedDocumentTypeEntry {
  documentType: ShipmentDocumentTypeDefinition;
  ownerCompanyId: string;
  ownerCompanyName: string;
  readOnly: true;
}

/** What deleting a document type cleaned up, so the UI can report it. */
export interface DocumentTypeCascadeSummary {
  documentConfigsUpdated: number;
  milestoneConfigsUpdated: number;
  milestonesUpdated: number;
}

// ============================================================================
// Milestone Audit Log Types
// ============================================================================

export type MilestoneAuditAction =
  | 'created'
  | 'status_changed'
  | 'edited'
  | 'deleted'
  | 'eta_set'
  | 'eta_updated'
  | 'auto_completed'
  | 'auto_in_progress'
  | 'reordered';

export interface MilestoneAuditChange {
  field: string;
  oldValue?: unknown;
  newValue?: unknown;
  label?: string;
}

export interface MilestoneAuditLogEntry {
  id: string;
  companyId: string;
  shipmentId: string;
  milestoneId: string;
  milestoneName: string;
  action: MilestoneAuditAction;
  actor: string;
  actorType: 'user' | 'system' | 'partner';
  timestamp: string;
  /**
   * Orders entries one operation wrote together, whose timestamps can share a
   * millisecond. The API sorts on it; absent on standalone entries.
   */
  sequence?: number;
  changes?: MilestoneAuditChange[];
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Container register (GET /api/shipments/containers)
// ============================================================================

/** One container, flat, joined to its shipment's references, route and dates. */
export interface ContainerRegisterRow {
  id: string;
  containerNumber: string;
  size: ContainerSize;
  containerType: ContainerType;
  isoEquipmentCode?: string;
  status: ContainerStatus;
  sealNumber?: string;
  grossWeight?: number;
  grossWeightUnit?: string;

  shipmentId: string;
  shipmentNumber?: string;
  shipmentType?: ShipmentType;
  shipmentStatus?: ShipmentStatus;
  blNumber?: string;
  bookingReference?: string;
  carrierName?: string;
  originLocation?: string;
  destinationLocation?: string;

  estimatedDeparture?: string;
  actualDeparture?: string;
  estimatedArrival?: string;
  etaLive?: string;
  actualArrival?: string;
  dischargeDate?: string;
  gateOutDate?: string;
  emptyReturnDate?: string;
}

export interface ContainerRegisterFilter {
  search?: string;
  status?: ContainerStatus;
  size?: ContainerSize;
  shipmentType?: ShipmentType;
}

export type ContainerRegisterQuery = ListQueryParams<ContainerRegisterFilter>;
